import { Link } from 'react-router-dom';
import { Plus, Music, Calendar, Users, UserPlus, Check, X, Search, MapPin, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useMyBands } from '../../hooks/useBands';
import { useMyBandJoinRequests, approveJoinRequest, rejectJoinRequest, submitJoinRequest } from '../../hooks/useJoinRequests';
import { useAuth } from '../../hooks/useAuth';
import { getInitials } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { useState, useEffect, useRef } from 'react';
import type { Band } from '../../types/database';

interface SearchResultBand extends Band {
  joinStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  isMember?: boolean;
}

export default function DashboardPage() {
  const { bands, loading } = useMyBands();
  const { user } = useAuth();
  const { requests, refetch: refetchRequests } = useMyBandJoinRequests();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Find & Join a Band state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultBand[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [joinModalBand, setJoinModalBand] = useState<SearchResultBand | null>(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounced band search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (!user) return;
      setSearchLoading(true);
      const q = searchQuery.trim();

      // Search public bands
      const { data: foundBands } = await supabase
        .from('bands')
        .select('*')
        .eq('is_public', true)
        .or(`name.ilike.%${q}%,location_name.ilike.%${q}%,location_country.ilike.%${q}%`)
        .order('name')
        .limit(10);

      if (!foundBands || foundBands.length === 0) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }

      // Check membership status for each found band
      const bandIds = foundBands.map((b) => b.id);
      const { data: memberships } = await supabase
        .from('band_members')
        .select('band_id')
        .eq('user_id', user.id)
        .eq('is_accepted', true)
        .in('band_id', bandIds);

      const memberBandIds = new Set((memberships ?? []).map((m) => m.band_id));

      // Check existing join requests
      const { data: existingRequests } = await supabase
        .from('join_requests')
        .select('band_id, status')
        .eq('user_id', user.id)
        .in('band_id', bandIds);

      const requestMap = new Map<string, string>();
      (existingRequests ?? []).forEach((r) => requestMap.set(r.band_id, r.status));

      const results: SearchResultBand[] = (foundBands as Band[]).map((b) => ({
        ...b,
        isMember: memberBandIds.has(b.id),
        joinStatus: (requestMap.get(b.id) as SearchResultBand['joinStatus']) ?? 'none',
      }));

      setSearchResults(results);
      setSearchLoading(false);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery, user]);

  async function handleJoinRequest() {
    if (!user || !joinModalBand) return;
    setJoinLoading(true);
    setJoinError('');
    const { error } = await submitJoinRequest(joinModalBand.id, user.id, joinMessage);
    if (error) {
      setJoinError(error);
    } else {
      // Update the search result to show pending status
      setSearchResults((prev) =>
        prev.map((b) => b.id === joinModalBand.id ? { ...b, joinStatus: 'pending' as const } : b)
      );
      setJoinModalBand(null);
      setJoinMessage('');
    }
    setJoinLoading(false);
  }

  async function handleApprove(req: typeof requests[0]) {
    if (!user) return;
    setActionLoading(req.id);
    await approveJoinRequest(req.id, req.band_id, req.user_id, user.id);
    refetchRequests();
    setActionLoading(null);
  }

  async function handleReject(req: typeof requests[0]) {
    if (!user) return;
    setActionLoading(req.id);
    await rejectJoinRequest(req.id, user.id);
    refetchRequests();
    setActionLoading(null);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">My Bands</h1>
        <Link
          to="/band/new"
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Create Band
        </Link>
      </div>

      {/* Join Request Alerts */}
      {requests.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-indigo-400" />
            Join Requests
            <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
              {requests.length}
            </span>
          </h2>
          <div className="space-y-3">
            {requests.map((req) => (
              <div key={req.id} className="bg-gray-900 border border-indigo-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">
                        {req.profile?.display_name || req.profile?.email || 'Unknown user'}
                      </span>
                      <span className="text-gray-400 text-sm">wants to join</span>
                      <span className="font-medium text-indigo-400">
                        {req.band?.name || 'your band'}
                      </span>
                    </div>
                    {req.message && (
                      <p className="text-gray-400 text-sm mt-1">"{req.message}"</p>
                    )}
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(req.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleApprove(req)}
                      disabled={actionLoading === req.id}
                      className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition"
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(req)}
                      disabled={actionLoading === req.id}
                      className="flex items-center gap-1.5 bg-gray-700 hover:bg-red-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition"
                    >
                      <X className="w-4 h-4" />
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Find & Join a Band */}
      <div className="mb-8">
        <button
          onClick={() => setShowSearch(!showSearch)}
          className="flex items-center gap-2 text-sm font-medium text-indigo-400 hover:text-indigo-300 transition mb-3"
        >
          <Search className="w-4 h-4" />
          {showSearch ? 'Hide search' : 'Find & join a band'}
        </button>

        {showSearch && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by band name, city, or country..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                autoFocus
              />
            </div>

            {searchLoading && (
              <div className="flex items-center gap-2 text-gray-400 text-sm mt-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Searching...
              </div>
            )}

            {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
              <p className="text-gray-500 text-sm mt-4">No bands found for "{searchQuery}"</p>
            )}

            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                {searchResults.map((band) => (
                  <div
                    key={band.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition"
                  >
                    {band.logo_url ? (
                      <img src={band.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-gray-400 text-xs font-bold">{getInitials(band.name)}</span>
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <Link to={`/band/${band.slug}`} className="font-medium text-sm hover:text-indigo-400 transition">
                        {band.name}
                      </Link>
                      <div className="flex items-center gap-2 text-gray-500 text-xs mt-0.5">
                        {band.location_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {band.location_name}
                            {band.location_country && `, ${band.location_country}`}
                          </span>
                        )}
                        {band.genres?.length > 0 && (
                          <span className="text-gray-600">{band.genres.slice(0, 2).join(', ')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      {band.isMember ? (
                        <span className="inline-flex items-center gap-1 text-green-400 text-xs bg-green-900/30 px-2.5 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Member
                        </span>
                      ) : band.joinStatus === 'pending' ? (
                        <span className="inline-flex items-center gap-1 text-yellow-400 text-xs bg-yellow-900/30 px-2.5 py-1 rounded-full">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      ) : band.joinStatus === 'approved' ? (
                        <span className="inline-flex items-center gap-1 text-green-400 text-xs bg-green-900/30 px-2.5 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" />
                          Approved
                        </span>
                      ) : band.joinStatus === 'rejected' ? (
                        <span className="inline-flex items-center gap-1 text-red-400 text-xs bg-red-900/30 px-2.5 py-1 rounded-full">
                          <XCircle className="w-3 h-3" />
                          Declined
                        </span>
                      ) : (
                        <button
                          onClick={() => { setJoinModalBand(band); setJoinError(''); }}
                          className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1.5 rounded-lg font-medium transition"
                        >
                          <UserPlus className="w-3 h-3" />
                          Request to Join
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Join Request Modal */}
      {joinModalBand && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-1">Request to Join</h2>
            <p className="text-indigo-400 font-medium mb-4">{joinModalBand.name}</p>
            <p className="text-gray-400 text-sm mb-4">
              The band owner will review your request. You can include an optional message.
            </p>

            {joinError && (
              <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
                {joinError}
              </div>
            )}

            <textarea
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value)}
              rows={3}
              placeholder="Tell the band why you'd like to join (optional)..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-4"
            />

            <div className="flex gap-3">
              <button
                onClick={() => { setJoinModalBand(null); setJoinError(''); setJoinMessage(''); }}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleJoinRequest}
                disabled={joinLoading}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg text-sm font-medium transition"
              >
                {joinLoading ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bands.length === 0 ? (
        <div className="text-center py-16">
          <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No bands yet</h2>
          <p className="text-gray-400 mb-6">
            Create your first band to start managing gigs, members, and setlists.
          </p>
          <Link
            to="/band/new"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition"
          >
            <Plus className="w-5 h-5" />
            Create Your First Band
          </Link>
        </div>
      ) : (
        <div className="grid gap-4">
          {bands.map((band) => (
            <Link
              key={band.id}
              to={`/band/${band.id}/manage`}
              className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-indigo-500/50 transition group"
            >
              <div className="flex items-center gap-4">
                {band.logo_url ? (
                  <img
                    src={band.logo_url}
                    alt={band.name}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gray-800 flex items-center justify-center group-hover:bg-gray-750">
                    <span className="text-gray-400 font-bold">{getInitials(band.name)}</span>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold group-hover:text-indigo-400 transition">
                    {band.name}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                    {band.location_name && <span>{band.location_name}</span>}
                    {band.genres.length > 0 && (
                      <span>{band.genres.slice(0, 3).join(', ')}</span>
                    )}
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-4 text-gray-500">
                  <Calendar className="w-5 h-5" />
                  <Users className="w-5 h-5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
